import type { GoogleGenAI, ThinkingLevel } from '@google/genai';

// Wire literals keep this helper runtime-independent from SDK enums; other
// modules may mock @google/genai without needing to reproduce enum exports.
const ARGON_THINKING_LEVEL = { MINIMAL: 'MINIMAL', LOW: 'LOW', MEDIUM: 'MEDIUM', HIGH: 'HIGH' } as const;

export const ARGON_MODEL = 'gemini-4-argon';
export const ARGON_NON_STREAMING_OUTPUT_LIMIT = 32_768;
export const ARGON_STREAMING_OUTPUT_LIMIT = 65_536;

type GenerateParams = Parameters<GoogleGenAI['models']['generateContent']>[0];
type GenerateResponse = Awaited<ReturnType<GoogleGenAI['models']['generateContent']>>;
type GenerateChunk = GenerateResponse;
type GenerateStream = Awaited<ReturnType<GoogleGenAI['models']['generateContentStream']>>;

export function isArgonModel(model: unknown): boolean {
  return typeof model === 'string' && model.toLowerCase() === ARGON_MODEL;
}

/** Argon-only request normalization. Other models retain the exact input object. */
export function normalizeArgonRequest(
  params: GenerateParams,
  mode: 'non-streaming' | 'streaming' = 'non-streaming',
): GenerateParams {
  if (!isArgonModel(params.model)) return params;
  const config = params.config ?? {};
  if (config.cachedContent != null) {
    throw new Error('Gemini 4 Argon does not support explicit cachedContent; send the complete inline prompt.');
  }
  const outputLimit = mode === 'streaming' ? ARGON_STREAMING_OUTPUT_LIMIT : ARGON_NON_STREAMING_OUTPUT_LIMIT;
  if (config.maxOutputTokens != null && config.maxOutputTokens > outputLimit) {
    throw new Error(`Gemini 4 Argon ${mode} maxOutputTokens exceeds ${outputLimit}.`);
  }

  const normalized = { ...config };
  delete normalized.temperature;
  delete normalized.topP;
  delete normalized.topK;

  const thinkingConfig = { ...(config.thinkingConfig ?? {}) };
  const hasLegacyBudget = typeof thinkingConfig.thinkingBudget === 'number';
  const requested = thinkingConfig.thinkingLevel;
  delete thinkingConfig.thinkingBudget;
  // SDK 1.47 types this field as ThinkingLevel; the assertion binds the exact
  // documented wire literal while avoiding a runtime dependency on the enum.
  let level: ThinkingLevel = ARGON_THINKING_LEVEL.MEDIUM as ThinkingLevel;
  const requestedWireLevel = String(requested);
  if (requestedWireLevel === ARGON_THINKING_LEVEL.MINIMAL) level = ARGON_THINKING_LEVEL.LOW as ThinkingLevel;
  else if (requestedWireLevel === ARGON_THINKING_LEVEL.LOW) level = ARGON_THINKING_LEVEL.LOW as ThinkingLevel;
  else if (requestedWireLevel === ARGON_THINKING_LEVEL.MEDIUM) level = ARGON_THINKING_LEVEL.MEDIUM as ThinkingLevel;
  else if (requestedWireLevel === ARGON_THINKING_LEVEL.HIGH) level = ARGON_THINKING_LEVEL.HIGH as ThinkingLevel;
  else if (hasLegacyBudget) level = ARGON_THINKING_LEVEL.LOW as ThinkingLevel;

  return {
    ...params,
    config: { ...normalized, thinkingConfig: { ...thinkingConfig, thinkingLevel: level } },
  };
}

export class GeminiIncompleteResponseError extends Error {
  readonly response: GenerateResponse;
  readonly reasons: string[];
  readonly code = 'GEMINI_INCOMPLETE_RESPONSE';

  constructor(response: GenerateResponse, reasons: string[]) {
    super(`Gemini response is incomplete (${reasons.join(', ')}); completion is required.`);
    this.name = 'GeminiIncompleteResponseError';
    this.response = response;
    this.reasons = reasons;
  }
}

/** Run after response observation/linking so usage metadata remains available. */
export function assertArgonResponseComplete<T extends GenerateResponse>(
  model: string,
  response: T,
): T {
  if (!isArgonModel(model)) return response;
  const reasons = (response.candidates ?? [])
    .map((candidate) => candidate.finishReason)
    .filter((reason): reason is NonNullable<typeof reason> =>
      String(reason) === 'MAX_TOKENS' || String(reason) === 'CONTINUATION',
    );
  if (reasons.length) throw new GeminiIncompleteResponseError(response, reasons.map(String));
  return response;
}

/** Preserve the SDK iterator lifecycle while checking each delivered terminal chunk.
 * The shared-client facade wraps iterator operations in the existing ALS scope;
 * this wrapper delegates to those methods without replacing their context.
 */
export function guardArgonStream(
  model: string,
  stream: GenerateStream,
): GenerateStream {
  if (!isArgonModel(model)) return stream;
  // SDK stream objects are AsyncGenerators. Use the passed instance directly:
  // its asyncIterator may return an underlying generator and bypass a scoped wrapper.
  const iterator = stream;
  const wrapped = {
    [Symbol.asyncIterator]() { return this; },
    next(...args: Parameters<GenerateStream['next']>) {
      return iterator.next(...args).then(async (result) => {
        if (!result.done) {
          try { assertArgonResponseComplete(model, result.value as GenerateChunk); }
          catch (error) {
            // A rejected next() does not guarantee the consumer calls return().
            // Close upstream now; retain the original incomplete-response error.
            try { await iterator.return(undefined); } catch { /* preserve original error */ }
            throw error;
          }
        }
        return result;
      });
    },
    return(...args: Parameters<GenerateStream['return']>) {
      return iterator.return(...args);
    },
    throw(...args: Parameters<GenerateStream['throw']>) {
      return iterator.throw(...args);
    },
  };
  return wrapped as GenerateStream;
}
